import { createContext, useContext } from "react";
import { createSignal } from "react-alien-signals";
import { create, createStore } from "zustand";
import { persist } from "zustand/middleware";
import { DEFAULT_COLOR_THEME } from "@/constants";
import type {
  TenantInfo,
  AppearanceConfig,
  AgentAction,
  Agent,
  ConversationMessage,
  User,
  AgentInfo,
  Product,
  AddOn,
  BrandKit,
  VoiceAgent,
  VoiceMode,
  TenantUsage,
  IntegrationConnection
} from "@/lib/api/schema";
import type { WhatsAppVoiceAgent } from "@/types/whatsappVoiceAgent";
import {
  PlanType,
  UsageCategory,
  AnalyticsType,
  LeadCollectionType,
  TrainMatType,
  DealValueRange,
  SalesCycleLength,
  SalesCurrency
} from "@/lib/api/schema";
import { type ApiBuildAgentConfig } from "@/types/buildAgent";
import { formatBrandKit } from "@/utils/brandKit";
import { formatBuildAgentConfig } from "@/utils/buildMyAgent";

export enum ChatMode {
  Widget = "widget",
  Nav = "ai_bar"
}

export const InitVoiceAgent = {
  id: "",
  platform_agent_id: "",
  is_enabled: false,
  voice_mode_id: "",
  language: "babel",
  strategy: {
    fields: []
  },
  maximum_session_time: 300,
  inactivity_check_delay: 0,
  max_retries: 0,
  inactivity_check_message: "",
  is_deleted: false,
  created_at: "",
  updated_at: "",
  voice_mode: {
    id: "",
    name: "",
    gender: 1,
    description: "",
    tag: "",
    created_at: "",
    updated_at: ""
  }
};

export const initPageStore = (
  agent: null | Agent = null,
  voiceAgent: null | VoiceAgent = null,
  whatsappVoiceAgent: null | WhatsAppVoiceAgent = null
) =>
  createStore<{
    title: string;
    description: string;
    token: string;
    agentMode: ChatMode;
    isLoading: boolean;
    agent: null | Agent;
    setAgent: (agent: Agent) => void;
    actions: AgentAction[];
    showSignOutDialog: boolean;
    showDeleteAccountDialog: boolean;
    agentUpdatedAt: string;
    isConfigUpdated: boolean;
    isBrandKitUpdated: boolean;
    isCrawling: boolean;
    showChangePasswordDialog: boolean;
    voiceAgent: VoiceAgent | null;
    setVoiceAgent: (voiceAgent: VoiceAgent | null) => void;
    whatsappVoiceAgent: WhatsAppVoiceAgent | null;
    setWhatsappVoiceAgent: (
      whatsappVoiceAgent: WhatsAppVoiceAgent | null
    ) => void;
    voiceModes: VoiceMode[];
    setVoiceModes: (voiceModes: VoiceMode[]) => void;
    update: (key: string, newVal: any) => void;
    newConversationStarter: string;
    brandkitTab: string;
    isInstallDrawerOpen: boolean;
  }>((set) => ({
    title: "Astra",
    description: "Astra is a platform for creating and managing AI agents.",
    token: "",
    agentMode: ChatMode.Nav,
    isLoading: true,
    agent,
    setAgent: (agent) => set({ agent }),
    actions: [] as AgentAction[],
    showSignOutDialog: false,
    showDeleteAccountDialog: false,
    agentUpdatedAt: "2025-04-29T13:22:03.324261Z",
    isConfigUpdated: false,
    isBrandKitUpdated: false,
    isCrawling: false,
    showChangePasswordDialog: false,
    voiceAgent: voiceAgent || InitVoiceAgent,
    setVoiceAgent: (voiceAgent) => set({ voiceAgent }),
    whatsappVoiceAgent: whatsappVoiceAgent || null,
    setWhatsappVoiceAgent: (whatsappVoiceAgent) => set({ whatsappVoiceAgent }),
    voiceModes: [],
    setVoiceModes: (voiceModes) => set({ voiceModes }),
    update: (key: string, newVal) => set(() => ({ [key]: newVal })),
    newConversationStarter: "",
    brandkitTab: "branding",
    isInstallDrawerOpen: false
  }));

export const PageCtx = createContext<ReturnType<typeof initPageStore> | null>(
  null
);

export const usePageStore = () => {
  const store = useContext(PageCtx);
  if (!store)
    throw new Error("usePageStore must be invoked within a StoreProvider");
  return store;
};

export const useAgentDialog = create<{
  renameState: {
    open: boolean;
    id: string;
    name: string;
    inputName: string;
    description: string;
    isLoading: boolean;
  };
  updateRename: (newVal: any) => void;
  deleteState: {
    open: boolean;
    id: string;
    name: string;
    inputName: string;
    isLoading: boolean;
  };
  updateDelete: (newVal: any) => void;
  agentLimit: boolean;
  setAgentLimit: (agentLimit: boolean) => void;
  integrationLimit: boolean;
  setIntegrationLimit: (integrationLimit: boolean) => void;
  customLimit: {
    open: boolean;
    title: string;
  };
  setCustomLimit: (customLimit: { open: boolean; title: string }) => void;
  freeTrialSignUp: boolean;
  setFreeTrialSignUp: (freeTrialSignUp: boolean) => void;
  freeTrialLast: boolean;
  setFreeTrialLast: (freeTrialLast: boolean) => void;
  freeTrialPro: boolean;
  setFreeTrialPro: (freeTrialPro: boolean) => void;
  freeTrialUpgrade: boolean;
  setFreeTrialUpgrade: (freeTrialUpgrade: boolean) => void;
  contactUs: boolean;
  setContactUs: (contactUs: boolean) => void;
}>((set) => ({
  renameState: {
    open: false,
    id: "",
    name: "",
    inputName: "",
    description: "",
    isLoading: false
  },
  updateRename: (newVal) =>
    set((state) => ({ renameState: { ...state.renameState, ...newVal } })),
  deleteState: {
    open: false,
    id: "",
    name: "",
    inputName: "",
    isLoading: false
  },
  updateDelete: (newVal) =>
    set((state) => ({ deleteState: { ...state.deleteState, ...newVal } })),
  agentLimit: false,
  setAgentLimit: (agentLimit) => set({ agentLimit }),
  integrationLimit: false,
  setIntegrationLimit: (integrationLimit) => set({ integrationLimit }),
  customLimit: {
    open: false,
    title: ""
  },
  setCustomLimit: (customLimit) => set({ customLimit }),
  freeTrialSignUp: false,
  setFreeTrialSignUp: (freeTrialSignUp) => set({ freeTrialSignUp }),
  freeTrialLast: false,
  setFreeTrialLast: (freeTrialLast) => set({ freeTrialLast }),
  freeTrialPro: false,
  setFreeTrialPro: (freeTrialPro) => set({ freeTrialPro }),
  freeTrialUpgrade: false,
  setFreeTrialUpgrade: (freeTrialUpgrade) => set({ freeTrialUpgrade }),
  contactUs: false,
  setContactUs: (contactUs) => set({ contactUs })
}));

export const criteriaListOpenStore = createSignal<{ [key: string]: boolean }>(
  {}
);

export const initConfig = {
  appearance_config: {
    widget: {
      name: "Agent Widget",
      welcome_message: {
        message: "",
        popup_mode: false
      },
      conversation_starter: {
        messages: [],
        popup_mode: false
      },
      display_form_immediately: false,
      brand_logo: {
        url: ""
      },
      color_theme: {
        primary_color: DEFAULT_COLOR_THEME // Default color example
      },
      disclaim_text: ""
    },
    ai_bar: {
      name: "",
      welcome_message: {
        message: "",
        popup_mode: false
      },
      conversation_starter: {
        messages: null,
        popup_mode: false
      },
      display_form_immediately: false,
      brand_logo: {
        url: ""
      },
      color_theme: {
        primary_color: ""
      },
      disclaim_text: ""
    }
  }
};

export const initConfigStore = (
  config: ApiBuildAgentConfig | null,
  brandKit: BrandKit | null
) =>
  createStore<{
    brandKit: BrandKit;
    setBrandKit: (brandKit: BrandKit) => void;
    updateBrandKit: (brandKit: BrandKit) => void;
    updateAppearance: (newVal: AppearanceConfig) => void;
    config: ApiBuildAgentConfig;
    // If the front end updates the data by itself, please use this modification.
    updateConfig: (
      newVal:
        | Partial<ApiBuildAgentConfig>
        | ((prevConfig: ApiBuildAgentConfig) => Partial<ApiBuildAgentConfig>)
    ) => void;
    // As long as the data is obtained from the API interface, this method is used to modify it uniformly
    setConfig: (newVal: ApiBuildAgentConfig) => void;
  }>((set) => ({
    brandKit: brandKit || {
      name: "",
      domain: "",
      claimed: false,
      description: "",
      longDescription: "",
      appearance_config: initConfig.appearance_config,
      custom_fields: {},
      sales_config: {
        typical_deal_value: DealValueRange.Under1K,
        sales_cycle_length: SalesCycleLength.Monthly,
        deal_value_currency: SalesCurrency.USD,
        decision_maker_titles: [],
        geographic_focus: [],
        ideal_customer_profile: [],
        segments: [],
        verticals: []
      },
      links: [],
      logos: [],
      colors: [],
      images: [],
      company: {
        employees: 0,
        financialIdentifiers: {
          isin: [],
          ticker: []
        },
        foundedYear: 0,
        industries: [
          {
            id: "",
            score: 0,
            name: "",
            slug: "",
            emoji: "",
            parent: {
              id: "",
              score: 0,
              slug: "",
              name: "",
              emoji: "",
              parent: null
            }
          }
        ],
        kind: "",
        location: {
          city: "",
          country: "",
          countryCode: "",
          region: "",
          state: "",
          subregion: ""
        }
      }
    },
    setBrandKit: (brandKit) => set({ brandKit: formatBrandKit(brandKit) }),
    updateBrandKit: (brandKit) => set({ brandKit }),
    updateAppearance: (newVal) =>
      set((state) => ({
        brandKit: {
          ...state.brandKit,
          appearance_config: { ...state.brandKit.appearance_config, ...newVal }
        }
      })),
    config: config || {
      instructions: "",
      communication_style: "friendly_professional",
      primary_goals: [],
      multi_lingual: false,
      /**
       * @deprecated onboarding_step field is being deprecated.
       * Always start from step 1 when entering build agent interface.
       */
      onboarding_step: 1,
      completed_onboarding_steps: [],
      form_display_config: {
        strategy: 0,
        trigger_frequency: 0,
        maximum_attempts: 1
      },
      lead_contact_fields: {
        fields: [],
        version: "",
        use_form: false,
        closable: true
      },
      qualification_config: {
        qualification_criteria: [],
        custom_criteria: []
      },
      actions: {
        action_mapping: {
          hot: [],
          warm: [],
          cold: [],
          unqualified: []
        }
      }
    },
    updateConfig: (newVal) => {
      if (typeof newVal === "function") {
        set((state) => ({
          config: { ...state.config, ...newVal(state.config) }
        }));
      } else {
        set((state) => ({ config: { ...state.config, ...newVal } }));
      }
    },
    setConfig: (newVal) => {
      set({ config: formatBuildAgentConfig(newVal) });
    }
  }));

export const AgentConfigCtx = createContext<ReturnType<
  typeof initConfigStore
> | null>(null);

export const useConfigStore = () => {
  const store = useContext(AgentConfigCtx);
  if (!store)
    throw new Error("useConfigStore must be invoked within a StoreProvider");
  return store;
};

export interface ConversationData {
  messages: ConversationMessage[];
  limit: number;
  has_more: boolean;
}

export const conversationStore = createSignal<ConversationData>({
  messages: [],
  limit: 20,
  has_more: false
});

export const tempConversationStore = createSignal<ConversationData>({
  messages: [],
  limit: 20,
  has_more: false
});

export const initUserStore = (user: User | null) =>
  createStore<{
    user: User | null;
    setUser: (user: User) => void;
    connects: IntegrationConnection[];
    setConnects: (connects: IntegrationConnection[]) => void;
    agents: AgentInfo[];
    setAgents: (agents: AgentInfo[]) => void;
    products: Product[];
    addOns: AddOn[];
    setAddOns: (addOns: AddOn[]) => void;
    setProducts: (products: Product[]) => void;
    usage: TenantUsage;
    setUsage: (usage: TenantUsage) => void;
    // Computed values as regular properties
    agentLimit: number;
    isAgentLimit: boolean;
    integrationLimit: number;
    integrationTypes: string[];
    get prices(): { [key: string]: Product };
  }>()(
    persist(
      (set, get) => ({
        user,
        setUser: (user) => set({ user }),
        connects: [],
        setConnects: (connects) => set({ connects }),
        agents: [],
        setAgents: (agents) => set({ agents }),
        products: [],
        setProducts: (products) => set({ products }),
        addOns: [],
        setAddOns: (addOns) => set({ addOns }),
        usage: {
          current_plan: PlanType.Free,
          integration_types: [],
          usage_configs: [
            {
              category: UsageCategory.USAGE_CREDIT,
              used_amount: "0",
              total_amount: "100",
              expiration_time: "2025-06-30T23:59:59"
            },
            {
              category: UsageCategory.USAGE_AI_AGENT,
              used_amount: "0",
              total_amount: "1",
              expiration_time: "2025-06-30T23:59:59"
            },
            {
              category: UsageCategory.USAGE_INTEGRATION,
              used_amount: "0",
              total_amount: "0",
              expiration_time: "2025-06-30T23:59:59"
            }
          ],
          agent_kb_usages: {},
          dataset_usages: {},
          lead_collections: [LeadCollectionType.LEADCOLLECTION_STANDARD_FORM],
          team_members: 1,
          train_material_types: [
            TrainMatType.TRAINMAT_QA_PAIR,
            TrainMatType.TRAINMAT_DOCUMENT,
            TrainMatType.TRAINMAT_WEBSITE
          ],
          lead_qualification: false,
          ai_web_widget: true,
          magic_spotlight: false,
          multi_lingual: false,
          conversation_insights: false,
          analytics: AnalyticsType.ANALYTICS_BASIC,
          voice_agent: false,
          custom_webhook: false,
          custom_branding_footer: false,
          updated_at: "2025-06-27T06:39:18.478119735Z"
        },
        setUsage: (usage) => {
          const computed = computeUsageLimits(usage);
          set({
            usage,
            ...computed
          });
        },

        // Initialize computed values based on initial usage
        ...(() => {
          const initialUsage = {
            current_plan: PlanType.Free,
            team_members: 1,
            integration_types: [],
            usage_configs: [
              {
                category: UsageCategory.USAGE_CREDIT,
                used_amount: "0",
                total_amount: "100",
                expiration_time: "2025-06-30T23:59:59"
              },
              {
                category: UsageCategory.USAGE_AI_AGENT,
                used_amount: "0",
                total_amount: "1",
                expiration_time: "2025-06-30T23:59:59"
              },
              {
                category: UsageCategory.USAGE_INTEGRATION,
                used_amount: "0",
                total_amount: "0",
                expiration_time: "2025-06-30T23:59:59"
              }
            ],
            agent_kb_usages: {},
            dataset_usages: {},
            lead_collections: [LeadCollectionType.LEADCOLLECTION_STANDARD_FORM],
            train_material_types: [
              TrainMatType.TRAINMAT_QA_PAIR,
              TrainMatType.TRAINMAT_DOCUMENT,
              TrainMatType.TRAINMAT_WEBSITE
            ],
            lead_qualification: false,
            ai_web_widget: true,
            magic_spotlight: false,
            multi_lingual: false,
            conversation_insights: false,
            analytics: AnalyticsType.ANALYTICS_BASIC,
            voice_agent: false,
            custom_webhook: false,
            custom_branding_footer: false,
            updated_at: "2025-06-27T06:39:18.478119735Z"
          };
          return computeUsageLimits(initialUsage);
        })(),

        get prices() {
          return get().products.reduce(
            (acc, product) => ({
              ...acc,
              [`${product.plan}_${product.period}`]: product
            }),
            {}
          );
        }
      }),
      {
        name: "user-state"
        // storage: createJSONStorage(() => sessionStorage),
      }
    )
  );

export const UserCtx = createContext<ReturnType<typeof initUserStore> | null>(
  null
);

// Helper function to check if a specific integration type is allowed
export const isIntegrationTypeAllowed = (
  integrationType: string,
  allowedTypes: string[]
) => {
  // If no allowed types are specified, deny all integrations
  if (!allowedTypes || allowedTypes.length === 0) {
    return false;
  }

  // Convert integration type to uppercase for comparison
  const normalizedType = integrationType.toUpperCase();

  // Check if the integration type is in the allowed list
  return allowedTypes.some(
    (allowedType) =>
      allowedType.toUpperCase().includes(normalizedType) ||
      normalizedType.includes(allowedType.toUpperCase())
  );
};

// Helper functions for computing usage limits
export const computeUsageLimits = (usage: TenantUsage) => {
  const agentUsage = usage.usage_configs.find(
    (v) => v.category === UsageCategory.USAGE_AI_AGENT
  );
  const integrationUsage = usage.usage_configs.find(
    (v) => v.category === UsageCategory.USAGE_INTEGRATION
  );

  const agentLimit = Number(agentUsage?.total_amount || "0");
  const isAgentLimit =
    agentLimit < 0
      ? false
      : Number(agentUsage?.used_amount || "0") >= agentLimit;

  const integrationLimit = Number(integrationUsage?.total_amount || "0");

  return {
    agentLimit,
    isAgentLimit,
    integrationLimit,
    integrationTypes: usage.integration_types || []
  };
};

export const useUserStore = () => {
  const store = useContext(UserCtx);
  if (!store)
    throw new Error("useUserStore must be invoked within a StoreProvider");
  return store;
};

export enum WebhookStatus {
  Enabled = "enabled",
  Disabled = "disabled"
}

export enum WebhookEventType {
  LeadCaptured = "lead_captured",
  MessageSent = "message_sent",
  MessageReceived = "message_received"
}

export interface Webhook {
  id: string;
  agent_id: string;
  agent_name: string;
  url: string;
  auth: {
    type: WebhookAuthenticationType;
    key?: string;
    value?: string;
  };
  status: WebhookStatus;
  event: WebhookEventType;
  created_at: string;
}

export interface WebhookEvent {
  event: WebhookEventType;
  schema: {
    properties: {
      [key: string]: {
        description: string;
        example: string;
        type: string;
      };
    };
    required: string[];
    type: string;
  };
}

export enum WebhookAuthenticationType {
  None = "none",
  APIKey = "api_key",
  CustomHeader = "custom"
}

export const initTestWebhook = {
  id: "",
  agent_id: "",
  agent_name: "",
  url: "",
  auth: {
    type: WebhookAuthenticationType.None
  },
  status: WebhookStatus.Enabled,
  created_at: "",
  event: WebhookEventType.LeadCaptured
};

export const useWebhookStore = create<{
  webhooks: Webhook[];
  setWebhooks: (webhooks: Webhook[]) => void;
  events: WebhookEvent[];
  setEvents: (events: WebhookEvent[]) => void;
  eventList: () => WebhookEventType[];
  showTable: boolean;
  setShowTable: (showTable: boolean) => void;
  showAddDialog: boolean;
  setShowAddDialog: (showAddDialog: boolean) => void;
  showTestDialog: boolean;
  setShowTestDialog: (showTestDialog: boolean) => void;
  testWebhook: Webhook;
  setTestWebhook: (testWebhook: Webhook) => void;
  showLogsTable: boolean;
  setShowLogsTable: (showLogsTable: boolean) => void;
}>((set, get) => ({
  webhooks: [],
  setWebhooks: (webhooks) => set({ webhooks }),
  events: [],
  setEvents: (events) => set({ events }),
  eventList: () => {
    return get().events.map((v) => v.event);
  },
  showTable: false,
  setShowTable: (showTable) => set({ showTable }),
  showAddDialog: false,
  setShowAddDialog: (showAddDialog) => set({ showAddDialog }),
  showTestDialog: false,
  testWebhook: initTestWebhook,
  setShowTestDialog: (showTestDialog) => set({ showTestDialog }),
  setTestWebhook: (testWebhook) => set({ testWebhook }),
  showLogsTable: false,
  setShowLogsTable: (showLogsTable) => set({ showLogsTable })
}));
